

























































































































































































































































































import { Component, Vue } from 'vue-property-decorator';
import { SysKBMediaModule } from '@/store/modules/sysKBMedia';
import Alert from '@/components/Shared/Alert.vue';
import MediaCard from '@/components/KnowledgeBase/MediaCard.vue';
import MediaUploadDialog from '@/components/KnowledgeBase/MediaUploadDialog.vue';
import GiganautVue from '@/components/Shared/GiganautVue';
import KBMediaListItem from '@/models/KnowledgeBase/KBMediaListItem';
import KBMedia from '@/models/KnowledgeBase/KBMedia';
import KBMediaType from '@/models/KnowledgeBase/KBMediaType';

@Component({
  name: 'MediaGallery',
  components: {
    Alert,
    MediaCard,
    MediaUploadDialog
  }
})
export default class MediaGallery extends GiganautVue {
  private searchTerm = '';
  private mediaTypeFilter: number | null = null;
  private tagFilter = '';
  private sortBy = 'originalFileName';
  private sortOrder = 'asc';
  private currentPage = 1;
  private limit = 50;
  private uploadDialog = false;
  private detailDialog = false;
  private selectedMedia: KBMedia | null = null;
  private editAltText = '';
  private editTags: string[] = [];
  private detailValid = false;
  private saving = false;
  private archiving = false;
  private republishing = false;
  private searchDebounceTimer: number | null = null;

  private mediaTypeOptions = [
    { text: 'All Types', value: null },
    { text: 'Images', value: KBMediaType.Image },
    { text: 'Videos', value: KBMediaType.Video },
    { text: 'Files', value: KBMediaType.File }
  ];

  private sortOptions = [
    { text: 'Name (A-Z)', value: 'originalFileName' },
    { text: 'Date Uploaded', value: 'createdDate' },
    { text: 'File Size', value: 'fileSize' }
  ];

  private sortOrderOptions = [
    { text: 'Ascending', value: 'asc' },
    { text: 'Descending', value: 'desc' }
  ];

  get loading(): boolean {
    return SysKBMediaModule.loadingMediaList;
  }

  get mediaList() {
    return SysKBMediaModule.mediaList;
  }

  get mediaItems(): KBMediaListItem[] {
    return this.mediaList?.items || [];
  }

  get totalPages(): number {
    if (!this.mediaList) return 0;
    return Math.ceil(this.mediaList.total / this.limit);
  }

  get offset(): number {
    return (this.currentPage - 1) * this.limit;
  }

  get availableTags(): string[] {
    const tags = new Set<string>();
    this.mediaItems.forEach(item => {
      item.tags?.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }

  created() {
    this.loadMediaList();
  }

  debouncedSearch() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = window.setTimeout(() => {
      this.currentPage = 1;
      this.loadMediaList();
    }, 300);
  }

  async loadMediaList() {
    const params: any = {
      limit: this.limit,
      offset: this.offset,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder
    };

    if (this.searchTerm) {
      params.searchTerm = this.searchTerm;
    }
    if (this.mediaTypeFilter !== null) {
      params.mediaType = this.mediaTypeFilter;
    }
    if (this.tagFilter) {
      params.tag = this.tagFilter;
    }

    await SysKBMediaModule.getMediaList(params);
  }

  onPageChange() {
    this.loadMediaList();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  openUploadDialog() {
    this.uploadDialog = true;
  }

  async onMediaUploaded(mediaId: string) {
    await this.loadMediaList();
    // Optionally view the newly uploaded media
    this.viewMediaById(mediaId);
  }

  async viewMedia(media: KBMediaListItem) {
    this.viewMediaById(media.id);
  }

  async viewMediaById(mediaId: string) {
    try {
      this.selectedMedia = await SysKBMediaModule.getMedia(mediaId);
      this.editAltText = this.selectedMedia.altText || '';
      this.editTags = this.selectedMedia.tags || [];
      this.detailDialog = true;
      this.pendingDialog = false;
    } catch (error) {
      console.error('Failed to load media:', error);
    }
  }

  async saveMetadata() {
    if (!this.selectedMedia) return;

    this.saving = true;
    try {
      await SysKBMediaModule.updateMetadata({
        mediaId: this.selectedMedia.id,
        request: {
          altText: this.editAltText,
          tags: this.editTags
        }
      });
      this.detailDialog = false;
      await this.loadMediaList();
    } catch (error) {
      console.error('Failed to save metadata:', error);
    } finally {
      this.saving = false;
    }
  }

  async archiveMedia() {
    if (!this.selectedMedia) return;

    if (!confirm(`Are you sure you want to archive "${this.selectedMedia.originalFileName}"? This action cannot be undone.`)) {
      return;
    }

    this.archiving = true;
    try {
      await SysKBMediaModule.archiveMedia(this.selectedMedia.id);
      this.detailDialog = false;
      await this.loadMediaList();
    } catch (error) {
      console.error('Failed to archive media:', error);
    } finally {
      this.archiving = false;
    }
  }

  async republishContent() {
    if (!this.selectedMedia) return;

    this.republishing = true;
    try {
      const result = await SysKBMediaModule.republishContent(this.selectedMedia.id);
      alert(`Successfully republished ${result.contentCount} content items.`);
      // Refresh the selected media to update needsRepublish flag
      this.selectedMedia = await SysKBMediaModule.getMedia(this.selectedMedia.id);
    } catch (error) {
      console.error('Failed to republish content:', error);
    } finally {
      this.republishing = false;
    }
  }

  getMediaIcon(mediaType: number): string {
    switch (mediaType) {
      case KBMediaType.Image:
        return 'mdi-image';
      case KBMediaType.Video:
        return 'mdi-video';
      case KBMediaType.File:
        return 'mdi-file';
      default:
        return 'mdi-file';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  formatDate(date: Date | undefined): string {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}
